import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import ReCAPTCHA from 'react-google-recaptcha';
import { FaArrowRight } from '@react-icons/all-files/fa/FaArrowRight';
import useTranslate from '~hooks/useTranslate/useTranslate';
import { contactUsForm } from '../../../constants/forms';
import getField from 'gatsby-core-theme/src/components/organisms/form/fields';
import styles from './form.module.scss';

const FormComponent = ({
  formOptions = Object.values(contactUsForm)[0] || {},
  successMessage = 'Form has sent successfully',
  failMessage = 'Form has not sent, Got some error',
  validationMessage = 'Fill all the required fields.',
  submitUrl = '',
  hasButton = true,
  showButtonIcon = true,
  buttonLabel = 'Submit',
  disabled = true,
  showLabels = true,
  customeSubmit = null,
  customError = false,
  path = '',
}) => {  
  const recaptchaRef = useRef();
  const formRef = useRef();
  const [state, setState] = useState({
    loading: false,
    success: false,
    failed: false,
    isValid: true,
    isDisabled: disabled,
  });
  const values =
    formOptions.fields?.map((field) => ({
      [field.id]: field.value ? field.value : '',
    })) || [];
  // add recaptcha if its added in form options
  if (formOptions.hasReCAPTCHA) values.push({ gRecaptchaResponse: '' });
  const [elements, setElements] = useState(Object.assign({}, ...values));

  const handleChange = (name, value) => {
    setState({ ...state, [name]: value });
    setElements({ ...elements, [name]: value });
  };

  async function postData(url = '', data = {}) {
    return fetch(url, {
      method: 'POST',
      cache: 'no-cache',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(Object.fromEntries(data)),
    });
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isEmpty = (value) => value === '';
    const form = e.target;

    // Check if all required fields are empty
    const isValid = !Object.values(elements).some(isEmpty);
    setState({ ...state, loading: true, success: false, failed: false, isValid, isDisabled: true });

    try {
      let response;
      if (customeSubmit) {
        response = await customeSubmit(form);
      } else {
        response = await postData(submitUrl, new FormData(form));
      }
      setState({
        ...state,
        success: response.ok,
        failed: !response.ok,
        loading: false,
        isValid: true,
        isDisabled: true,
      });
    } catch (err) {
      // Error handling
      setState({
        ...state,
        failed: true,
        loading: false,
        isValid: true,
      });
    }

    recaptchaRef?.current?.reset();
  };

  function recaptchaOnChange(value) {
    // eslint-disable-next-line no-unused-expressions
    if (value) {
      setState({ ...state, isDisabled: false });
      setElements({ ...elements, gRecaptchaResponse: value });
    }
  }

  // form not specified on constant
  if (!Object.keys(formOptions).length) return;
  
  return (
    <div className={`${styles.formComponent || ''} `}>
      {formOptions?.title && (
        <span>
          {useTranslate(
            [`${path}_${formOptions.title?.translationKey}`, formOptions.title?.translationKey],
            formOptions.title?.label
          )}
        </span>
      )}
      {formOptions?.description && (
        <p>
          {useTranslate(
            [
              `${path}_${formOptions.description?.translationKey}`,
              formOptions.description?.translationKey,
            ],
            formOptions.description?.label
          )}
        </p>
      )}
      {formOptions?.fields && (
        <form
          ref={formRef}
          onSubmit={(e) => handleSubmit(e)}
          action={submitUrl}
          className={formOptions.twoCol ? styles.twoCol || '' : styles.singleCol || ''}
          target="_self"
        >
          {formOptions.fields?.map((field) => {
            const { id, translationKey, label, validations } = field;

            return (
              <div
                key={id}
                className={`${styles.formGroup || ''} ${styles[id] ? styles[id] : ''} ${
                  (!field.twoCol && styles.fullWidth) || ''
                }`}
              >
                {showLabels && label && (
                  <label htmlFor={id}>{useTranslate(translationKey, label)}</label>
                )}
                {elements && getField(field, handleChange, elements, state)}
                {validations && !state.isValid && elements[id] === '' && (
                  <span className={styles.alertWarningMessage || ''}>
                    {validations?.icon}
                    {useTranslate(validations?.translationKey, validations?.label)}
                  </span>
                )}
              </div>
            );
          })}
          {formOptions.hasReCAPTCHA && (
            <div
              className={`${styles.recaptcha || ''} ${
                (!state.isValid && elements.gRecaptchaResponse === '' && styles.invalid) || ''
              }`}
            >
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={`${process.env.RECAPTCHA_SITE_KEY}`}
                // eslint-disable-next-line react/jsx-no-bind
                onChange={recaptchaOnChange}
              />
              {!state.isValid &&
                elements.gRecaptchaResponse === '' &&
                formOptions?.reCaptcha?.validations && (
                  <span className={styles.alertWarningMessage || ''}>
                    {formOptions?.reCaptcha?.validations?.icon}
                    {useTranslate(
                      formOptions?.reCaptcha?.validations?.translationKey,
                      formOptions?.reCaptcha?.validations.label
                    )}
                  </span>
                )}
            </div>
          )}
          {hasButton && (
            <button disabled={state.loading} className={styles.formButton || ''} type="submit">
              {state.loading
                ? useTranslate('loading_btn', 'sending...')
                : useTranslate('send_button', buttonLabel)}
              {showButtonIcon ? (
                <FaArrowRight fontSize={20} title="Right-pointing Arrow Icon" />
              ) : null}
            </button>
          )}
          {!customError && (state.success || state.failed || !state.isValid) && (
            <div className={styles.formAlerts || ''}>
              {state.success && <div className={styles.alertSuccess || ''}>{successMessage}</div>}
              {state.failed && <div className={styles.alertDanger || ''}>{failMessage}</div>}
              {!state.isValid && (
                <div className={styles.alertWarning || ''}>{validationMessage}</div>
              )}
            </div>
          )}
        </form>
      )}
    </div>
  );
};

FormComponent.propTypes = {
  formOptions: PropTypes.shape({}),
  successMessage: PropTypes.string,
  failMessage: PropTypes.string,
  validationMessage: PropTypes.string,
  submitUrl: PropTypes.string.isRequired,
  hasButton: PropTypes.bool,
  buttonLabel: PropTypes.string,
  disabled: PropTypes.bool,
  showLabels: PropTypes.bool,
  showButtonIcon: PropTypes.bool,
  customError: PropTypes.bool,
  customeSubmit: PropTypes.func,
  path: PropTypes.string,
};
export default FormComponent;
